import { TFunction } from 'react-i18next';
import { MSAL_CONFIG } from '../providers/azure/auth-config';
import { PublicClientApplication } from '@azure/msal-browser';
import { TenantAddon } from '@bluedolphin/util-module-mf';

export type Application = {
    name: string;
    app: () => Promise<Window['System']['Module']>;
    activeWhen?: string[];
    customProps: {};
    addonIdentifiers?: { activeWhen: string[]; identifier: string }[];
};

/**
 * create MSAL config
 */
export function createMsalConfig(
    policy: string,
    tenant: string,
    clientId: string
) {
    const config = MSAL_CONFIG;
    config.auth.authority = policy;
    config.auth.knownAuthorities = [policy];
    config.auth.clientId = clientId;
    return config;
}

/**
 * create MSAL client
 */
export function createMsalClient(config: typeof MSAL_CONFIG) {
    return new PublicClientApplication(config);
}

/**
 * prevents custom props from being forwarded to the dom
 */
export function shouldForwardProp(...props: string[]) {
    return function preventProps(prop: string) {
        let value: boolean;
        for (const key of props) {
            if (key !== prop) {
                value = true;
            } else {
                value = false;
                break;
            }
        }
        return value;
    };
}

/**
 * sort function
 * @param arr
 * @returns
 */
export function sortOptons(arr: { label: string }[]) {
    return arr.sort((a, b) => {
        if (a.label < b.label) return -1;
        else if (a.label > b.label) return 1;
        else return 0;
    });
}

/**
 * translate each item in a list of select options
 * @param arr
 * @param t
 * @returns
 */
export function translate(arr, t) {
    return arr.map((item) => ({ ...item, label: t(item.label) }));
}

/**
 * create update user object
 * @param userData
 * @param selectedItem
 * @param id
 * @param isAutocomplete
 * @returns updatedUser
 */
export type UserDetails = {
    function: string[];
    responsibilities: string[];
    department: string[];
    working_with: string[];
};

export type UserData = {
    profile: {
        details: UserDetails;
        version: string;
    };
    default_view_mode: boolean;
    first_name?: string;
    last_name?: string;
    email?: string;
};

export type ChangeValue = { value: string } | { value: string }[] | boolean;
export type ChangeProp = { [key: string]: ChangeValue };
/**
 * create updated user object
 * @param userData
 * @param changes
 * @returns
 */
/* eslint-disable */
export function createUpdatedUser(userData: UserData, changes?: ChangeProp) {
    const updatedUser = {
        function: userData.profile.details
            ? userData.profile.details.function.join()
            : '',
        responsibilities: userData.profile?.details
            ? userData.profile.details.responsibilities.join()
            : '',
        department: userData.profile?.details
            ? userData.profile.details.department.join()
            : '',
        working_with: userData.profile.details
            ? userData.profile.details.working_with.join()
            : '',
        version: userData.profile.version
            ? parseInt(userData.profile.version) + 1
            : 1,
        default_view_mode: userData.default_view_mode,
    };

    if (changes) {
        for (const [key, value] of Object.entries(changes)) {
            switch (key) {
                case 'function':
                    updatedUser.function = (value as { value: string }).value;
                    break;
                case 'responsibilities':
                    updatedUser.responsibilities = (
                        value as { value: string }[]
                    )
                        .map((item) => item.value)
                        .join();
                    break;
                case 'department':
                    updatedUser.department = (value as { value: string }[])
                        .map((item) => item.value)
                        .join();
                    break;
                case 'working_with':
                    updatedUser.working_with = (value as { value: string }[])
                        .map((item) => item.value)
                        .join();
                    break;
                case 'default_view_mode':
                    updatedUser.default_view_mode = value as boolean;
                    break;
                default:
                    break;
            }
        }
    }

    return updatedUser;
}

/**
 * handle alert
 */
export function AlertHandler(
    response,
    enqueueSnackbar,
    t: TFunction<'translation'>
) {
    if (response.data) {
        enqueueSnackbar(t('Updated Successfully'), {
            variant: 'success',
            autoHideDuration: 2000,
        });
    } else if (response.errorMessage) {
        enqueueSnackbar(t('Saving failed, please try again.'), {
            variant: 'error',
            autoHideDuration: 2000,
        });
    }
}

/**
 * stripes prefix from firstname
 */
export function stripPrefix(name) {
    if (name?.startsWith('(') && name?.includes(')')) {
        const closingParenIndex = name.indexOf(')');
        // Extract the substring after the closing parenthesis ")"
        return name.substring(closingParenIndex + 1).trim();
    } else {
        // Return the original name if it doesn't have the expected format
        return name;
    }
}

export function canIncludeAddonApp(
    addons: TenantAddon[],
    addonIdentifiers: Application['addonIdentifiers']
) {
    return addons.some(
        ({ identifier, is_available }) =>
            addonIdentifiers.find(
                (addonIdentifier) => addonIdentifier.identifier === identifier
            ) && is_available
    );
}

export function filterAvailableAddonIdentifiers(
    tenantAddons: TenantAddon[],
    addonIdentifiers: { identifier: string; activeWhen: any }[]
) {
    return addonIdentifiers?.filter(({ identifier }) =>
        tenantAddons.some(
            (addon) => addon.identifier === identifier && addon.is_available
        )
    );
}

export function stripApplication(
    tenantAddons: TenantAddon[],
    {
        name,
        app,
        customProps,
        activeWhen = [],
        addonIdentifiers = [],
    }: Application
): Application {
    const availableAddonIdentifiers = filterAvailableAddonIdentifiers(
        tenantAddons,
        addonIdentifiers
    );

    return {
        name,
        app,
        customProps,
        activeWhen: activeWhen.concat(
            ...availableAddonIdentifiers.map(({ activeWhen }) => activeWhen)
        ),
    };
}

/**
 * gets the active applications
 */
export function getActiveApplications(
    tenantAddons: TenantAddon[],
    applications: Application[]
) {
    const activeApps: Application[] = [];
    applications.forEach((app) => {
        if (!Array.isArray(app.addonIdentifiers) || !app?.addonIdentifiers[0]) {
            activeApps.push(stripApplication(tenantAddons, app));
        }

        if (app.addonIdentifiers) {
            if (
                canIncludeAddonApp(tenantAddons, app.addonIdentifiers) ||
                (Array.isArray(app.activeWhen) && app.activeWhen.length)
            ) {
                activeApps.push(stripApplication(tenantAddons, app));
            }
        }
    });
    return activeApps;
}

export type UserRole = {
    label: string;
    en: string;
    nl: string;
};
